/**
 * Model Loader
 * 
 * Wrapper around GLTFLoader with Draco decompression support.
 * Handles model loading, progress tracking, and error handling.
 */

import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import type { IAssetLoader, Bounds } from '../types';

export interface LoadedModel {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
  bounds: Bounds;
  meshes: Map<string, THREE.Mesh>;
  materials: Map<string, THREE.Material>;
}

export class ModelLoader implements IAssetLoader<LoadedModel> {
  private gltfLoader: GLTFLoader;
  private dracoLoader: DRACOLoader;
  private loadingManager: THREE.LoadingManager;
  
  constructor(dracoDecoderPath = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/') {
    this.loadingManager = new THREE.LoadingManager();
    
    // Set up DRACO loader for compressed models
    this.dracoLoader = new DRACOLoader(this.loadingManager);
    this.dracoLoader.setDecoderPath(dracoDecoderPath);
    this.dracoLoader.preload();
    
    // Set up GLTF loader
    this.gltfLoader = new GLTFLoader(this.loadingManager);
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
  }
  
  /**
   * Load a GLB/glTF model
   */
  public async load(
    url: string,
    onProgress?: (progress: number) => void
  ): Promise<LoadedModel> {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        url,
        (gltf: GLTF) => {
          try {
            const result = this.processModel(gltf);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        (event) => {
          if (onProgress && event.lengthComputable) {
            onProgress(event.loaded / event.total);
          }
        },
        (error) => {
          reject(new Error(`Failed to load model: ${url} - ${error}`));
        }
      );
    });
  }
  
  /**
   * Process loaded GLTF model (handles both .gltf and .glb formats)
   */
  private processModel(gltf: GLTF): LoadedModel {
    const scene = gltf.scene;
    const meshes = new Map<string, THREE.Mesh>();
    const materials = new Map<string, THREE.Material>();
    
    console.log('[ModelLoader] Processing model...');
    console.log('[ModelLoader] Scene children:', scene.children.length);
    
    // Track mesh counts per parent for proper naming
    const meshCountByParent = new Map<string, number>();
    let meshIndex = 0;
    
    // Traverse and collect meshes and materials
    scene.traverse((object) => {
      // Log all objects for debugging
      if (object.name) {
        console.log(`[ModelLoader] Object: "${object.name}" (type: ${object.type})`);
      }
      
      if (object instanceof THREE.Mesh) {
        // Get the logical parent name (for split multi-material meshes)
        const meshName = this.resolveMeshName(object, meshCountByParent, meshIndex++);
        object.name = meshName;
        
        meshes.set(meshName, object);
        
        // Enable shadows
        object.castShadow = true;
        object.receiveShadow = true;
        
        // Collect materials with detailed logging
        if (Array.isArray(object.material)) {
          console.log(`[ModelLoader] Mesh "${meshName}" has ${object.material.length} materials (multi-material)`);
          object.material.forEach((mat, index) => {
            const matName = mat.name || `${meshName}_material_${index}`;
            if (!mat.name) {
              mat.name = matName;
            }
            materials.set(matName, mat);
            this.logMaterialDetails(matName, mat);
          });
        } else if (object.material) {
          const matName = object.material.name || `${meshName}_material`;
          if (!object.material.name) {
            object.material.name = matName;
          }
          materials.set(matName, object.material);
          this.logMaterialDetails(matName, object.material);
        }
      }
    });
    
    // Calculate bounds
    const bounds = this.calculateBounds(scene);
    
    // Summary log
    console.log('[ModelLoader] ═══════════════════════════════════════');
    console.log('[ModelLoader] Model Processing Summary:');
    console.log(`[ModelLoader]   Total meshes: ${meshes.size}`);
    console.log(`[ModelLoader]   Total materials: ${materials.size}`);
    console.log(`[ModelLoader]   Animations: ${gltf.animations.length}`);
    console.log(`[ModelLoader]   Bounds: size=${bounds.size.map(v => v.toFixed(2)).join('x')}`);
    console.log('[ModelLoader] ═══════════════════════════════════════');
    console.log('[ModelLoader] Meshes:', Array.from(meshes.keys()));
    console.log('[ModelLoader] Materials:', Array.from(materials.keys()));
    
    return {
      scene,
      animations: gltf.animations,
      bounds,
      meshes,
      materials,
    };
  }
  
  /**
   * Resolve proper mesh name, handling GLTF's multi-material mesh splitting
   * 
   * When a mesh has multiple materials in Blender, GLTF exports it as separate
   * meshes with auto-generated names like "Cube001", "Cube001_1", etc.
   * This method tries to recover the original logical name from:
   * 1. The parent group name (if the mesh is inside a group)
   * 2. The base name without suffixes like _1, _2
   * 3. Falls back to the mesh's own name or generates one
   */
  private resolveMeshName(
    mesh: THREE.Mesh, 
    countByParent: Map<string, number>,
    fallbackIndex: number
  ): string {
    // Strategy 1: Check if parent has a meaningful name (common for split meshes)
    const parent = mesh.parent;
    if (parent && parent.name && parent.type === 'Group' && parent.name !== 'Scene') {
      // Use parent name directly - this is the original object name from Blender
      return parent.name;
    }
    
    // Strategy 2: Clean up GLTF auto-generated names
    // GLTF often creates names like "Cube001", "Cube001_1" for split meshes
    if (mesh.name) {
      // Check for patterns like "Name_1", "Name_2" or "Name001", "Name001_1"
      const splitPattern = /^(.+?)(?:_(\d+))?$/;
      const match = mesh.name.match(splitPattern);
      
      if (match) {
        const baseName = match[1];
        // Remove trailing numbers added by GLTF export (like Cube001 -> Cube)
        const cleanBase = baseName.replace(/(\d{2,})$/, '');
        
        if (cleanBase !== baseName) {
          // There were trailing numbers, this is likely a split mesh
          const count = countByParent.get(cleanBase) ?? 0;
          countByParent.set(cleanBase, count + 1);
          
          // Preserve material context in name if available
          const mat = mesh.material;
          const matName = Array.isArray(mat) ? mat[0]?.name : mat?.name;
          if (matName && !matName.includes('material_')) {
            return `${cleanBase}_${matName}`;
          }
          
          return count === 0 ? cleanBase : `${cleanBase}_part${count + 1}`;
        }
      }
      
      // Use original name if it seems intentional
      return mesh.name;
    }
    
    // Strategy 3: Fallback to indexed name
    return `mesh_${fallbackIndex}`;
  }
  
  /**
   * Log material details for debugging
   */
  private logMaterialDetails(name: string, mat: THREE.Material): void {
    const details: Record<string, unknown> = {
      type: mat.type,
      uuid: mat.uuid.substring(0, 8),
    };
    
    if (mat instanceof THREE.MeshStandardMaterial) {
      details.color = `#${mat.color.getHexString()}`;
      details.roughness = mat.roughness;
      details.metalness = mat.metalness;
      details.hasMap = !!mat.map;
      details.hasNormalMap = !!mat.normalMap;
      details.hasRoughnessMap = !!mat.roughnessMap;
      details.hasMetalnessMap = !!mat.metalnessMap;
      details.hasAoMap = !!mat.aoMap;
      details.hasEmissiveMap = !!mat.emissiveMap;
    }
    
    console.log(`[ModelLoader]   Material "${name}":`, details);
  }
  
  /**
   * Calculate bounding box of a model
   */
  private calculateBounds(object: THREE.Object3D): Bounds {
    const box = new THREE.Box3().setFromObject(object);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    
    box.getCenter(center);
    box.getSize(size);
    
    return {
      min: box.min.toArray() as [number, number, number],
      max: box.max.toArray() as [number, number, number],
      center: center.toArray() as [number, number, number],
      size: size.toArray() as [number, number, number],
    };
  }
  
  /**
   * Center a model at origin based on its bounds
   */
  public static centerModel(model: THREE.Object3D, bounds: Bounds): void {
    model.position.set(-bounds.center[0], -bounds.center[1], -bounds.center[2]);
  }
  
  /**
   * Scale model to fit within a target size
   */
  public static scaleToFit(model: THREE.Object3D, bounds: Bounds, targetSize: number): void {
    const maxDim = Math.max(...bounds.size);
    const scale = targetSize / maxDim;
    model.scale.setScalar(scale);
  }
  
  /**
   * Clean up resources
   */
  public dispose(): void {
    this.dracoLoader.dispose();
  }
}
